import { OktaAuth } from "@okta/okta-auth-js";
import config from "../config";

// Detects strings that look like an unsubstituted Azure DevOps token
// (e.g. the build-time placeholder used in src/config.js) or an
// unsubstituted Azure Pipelines macro (e.g. $(name)).
//
// IMPORTANT: this regex is written so its source text does NOT contain
// the literal hash-curly-clientId-curly substring. The Azure DevOps
// token-replace task scans every file in the deployment for that exact
// pattern and rewrites it with the value of the pipeline variable. If
// we wrote the placeholder as a plain string literal here, our own
// comparison would be rewritten to the real client ID at deploy time
// and every real value would then be misidentified as a placeholder.
const UNSUBSTITUTED_AZURE_TOKEN = /^#\{[^}]+\}$/;
const UNSUBSTITUTED_PIPELINE_MACRO = /^\$\([^)]+\)$/;

const isPlaceholderValue = (value) => {
  if (!value) {
    return true;
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) {
    return true;
  }

  return (
    UNSUBSTITUTED_AZURE_TOKEN.test(normalizedValue) ||
    UNSUBSTITUTED_PIPELINE_MACRO.test(normalizedValue)
  );
};

const readClientIdFromEnv = () => {
  const envCandidates = [
    process.env.REACT_APP_OKTA_CLIENT_ID,
    process.env.REACT_APP_BROKER_OKTA_CLIENT_ID,
  ];

  const validEnvClientId = envCandidates.find(
    (candidate) => !isPlaceholderValue(candidate)
  );

  return validEnvClientId ? validEnvClientId.trim() : null;
};

const getRedirectUri = () => {
  const envRedirectUri = process.env.REACT_APP_OKTA_REDIRECT_URI;
  if (!isPlaceholderValue(envRedirectUri)) {
    return envRedirectUri.trim();
  }

  return config.widget.redirectUri;
};

const getClientId = () => {
  const envClientId = readClientIdFromEnv();
  if (envClientId) {
    return envClientId;
  }

  if (!isPlaceholderValue(config.widget.clientId)) {
    return config.widget.clientId.trim();
  }

  return "";
};

export const getOktaWidgetConfig = () => ({
  ...config.widget,
  clientId: getClientId(),
  redirectUri: getRedirectUri(),
});

export const isOktaClientConfigured = () => !isPlaceholderValue(getClientId());

const _cfg = getOktaWidgetConfig();

export const oktaAuth = new OktaAuth({
  issuer: _cfg.issuer,
  clientId: _cfg.clientId,
  redirectUri: _cfg.redirectUri,
  scopes: _cfg.scopes,
  pkce: true,
});
